import { LoadingMessage } from '@sw-sw/lib-ui';
import { find } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { string } from 'prop-types';
import { useQuery } from 'react-query';
import { InspectionContext } from '../../../contexts/InspectionContext';
import findingApi from '../../../utils/api/finding';
import FindingCard from './FindingCard';

export default function FindingListing({ filter, status }) {
  const inspectionContext = useContext(InspectionContext);

  if (!inspectionContext.inspection.id) {
    throw new Error('inspection must be loaded in InspectionContext');
  }

  const { findings, setFilteredFindings } = inspectionContext;

  const findingsQuery = useQuery({
    queryFn: () => findingApi.index(inspectionContext.inspection.id),
    queryKey: ['inspectionFindings', inspectionContext.inspection.id],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (findingsQuery.isFetching) return;

    const queryData = Array.isArray(findingsQuery.data) ? findingsQuery.data : [];

    // Merge — preserves InspectionFindingModel (carry-over data) from findingApi.index
    let merged = (findings || []).map((finding) => ({
      ...finding,
      ...(find(queryData, { id: finding.id }) || {}),
    }));

    // Carry-over filter: if the inspection disables carry-overs, hide carried-over findings
    if (Array.isArray(merged) && merged.length && merged[0].disable_carry_overs) {
      merged = merged.filter((ele) => {
        if (ele.InspectionFindingModel) {
          return ele.InspectionFindingModel.carried_over === false;
        }
        return true;
      });
    }

    // Type filter
    if (filter && filter !== 'all') {
      merged = merged.filter((finding) => finding.type === filter);
    }

    // Status filter
    if (status === 'open') {
      merged = merged.filter((finding) => !finding.date_completed);
    } else if (status === 'closed') {
      merged = merged.filter((finding) => Boolean(finding.date_completed));
    }

    setFilteredFindings(merged);
  // findingsQuery.data intentionally omitted — changes already captured via isFetching transition
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingsQuery.isFetching, findings, filter, status, setFilteredFindings]);

  return (
    <section className='pure-g findings-listing'>
      {(findingsQuery.isLoading || findingsQuery.isError) ? (
        <LoadingMessage what='Findings' className='pure-u pure-u-1-1' />
      ) : (!inspectionContext.filteredFindings.length ? (
        <p className='pure-u pure-u-1-1'>No findings</p>
      ) : (
        inspectionContext.filteredFindings
          .sort((a, b) => b.number - a.number)
          .map((finding, index) => (
            <FindingCard key={index} finding={finding} />
          ))
      ))}
    </section>
  );
}

FindingListing.propTypes = {
  filter: string,
  status: string,
};

FindingListing.defaultProps = {
  filter: 'all',
  status: 'all',
};
